// University of Virginia: public college page (#2740), official facts only.
//
// Every figure below was read on 2026-10-01 from the source it names:
//  - UVA's Common Data Set 2025-2026 (Institutional Research and Analytics,
//    published as a web page whose sections B to J are embedded from UVA's
//    own reporting server, qlksnpn-apprd01.eservices.virginia.edu; the
//    source link is the ira.virginia.edu page that embeds them): C1
//    applicants, admits and enrollees for Fall 2025 with the residency
//    table, C8 test policy for Fall 2027, C21/C22 with early decision counts
//    for the Fall 2025 entering class (the CDS gives no early action counts).
//    Its G1 lists 2026-2027 charges for a typical student across the
//    schools, so the cost table uses Student Financial Services' Estimated
//    Undergraduate Cost of Attendance 2026-2027 for a first-year student in
//    the College of Arts and Sciences, which itemizes every line.
//  - admission.virginia.edu: Deadlines & Instructions (dates for this cycle,
//    the early plans, school of entry, the fall 2027 test optional policy).
//  - College Scorecard API, school id 234076 (net price by family income,
//    in-state).
// Nothing here is profile-derived, an odds figure or a student record.
// Re-check a figure against its source before editing it, and move
// `lastChecked` when you do.

export const SOURCES = {
    cds: {
        label: 'UVA Common Data Set 2025-2026',
        url: 'https://ira.virginia.edu/common-data-set/common-data-set-2025-2026',
        read: '2026-10-01',
    },
    instructions: {
        label: 'UVA Undergraduate Admission, Deadlines & Instructions',
        url: 'https://admission.virginia.edu/admission/deadlines-instructions',
        read: '2026-10-01',
    },
    coa: {
        label: 'UVA Student Financial Services, Estimated Undergraduate Cost of Attendance 2026-2027',
        url: 'https://sfs.virginia.edu/financial-aid-new-applicants/financial-aid-basics/estimated-undergraduate-cost-attendance-2026-2027',
        read: '2026-10-01',
    },
    scorecard: {
        label: 'College Scorecard, U.S. Department of Education',
        url: 'https://collegescorecard.ed.gov/school/?234076-University-of-Virginia-Main-Campus',
        read: '2026-10-15',
    },
};

const college = {
    slug: 'university-of-virginia',
    kbId: 'university_of_virginia',
    name: 'University of Virginia',
    shortName: 'UVA',
    aliases: ['UVA', 'UVa'],
    location: 'Charlottesville, VA',
    control: 'Public',
    lastChecked: '2026-10-01',
    sources: SOURCES,
    // From the KB's sourced branding (UVA brand colors page); decoration only.
    branding: {
        primary: '#E57200',
        secondary: '#232D4B',
        logo_url: 'https://storage.googleapis.com/college-counselor-assets/university-logos/university_of_virginia_logo.png',
    },

    admission: {
        entering: 'Fall 2025',
        cdsCycle: '2025-2026',
        applied: 64457,
        admitted: 10086,
        enrolled: 3978,
        source: 'cds',
        where: 'section C1',
        residency: [
            { group: 'In-state', applied: 17370, admitted: 4092 },
            { group: 'Out-of-state', applied: 39271, admitted: 5086 },
            { group: 'International', applied: 7816, admitted: 908 },
        ],
        residencySource: 'cds',
        residencyWhere: 'section C1, residency table',
    },
    // Acceptance by school or major: shown only where the school publishes it.
    byMajor: {
        published: false,
        note: 'UVA\'s Common Data Set reports one first-year acceptance rate for the whole university, with no rate by school or major.',
        source: 'cds',
    },
    earlyRounds: {
        title: 'Early decision',
        note: 'UVA also has a non-binding Early Action plan; the Common Data Set gives no Early Action counts.',
        year: 'Fall 2025',
        rows: [
            { plan: 'Early Decision', applied: 4969, admitted: 1376 },
        ],
        source: 'cds',
        where: 'sections C21 and C22',
    },

    programs: {
        entering: 'Fall 2027',
        intro: 'First-year applicants apply to one of five undergraduate schools or programs, and can list two academic interests:',
        rows: [
            { school: 'College of Arts & Sciences', requirement: 'Also the way in for the McIntire School of Commerce, the Batten School, the School of Data Science and Education programs other than Kinesiology. You declare a major at the end of your first or second year.' },
            { school: 'School of Architecture', requirement: 'Apply to it directly.' },
            { school: 'School of Engineering', requirement: 'Apply to it directly.' },
            { school: 'School of Nursing', requirement: 'Apply to it directly; you enter with your major declared.' },
            { school: 'Kinesiology', requirement: 'Apply to it directly; you enter with your major declared.' },
        ],
        note: 'You can change your school or program of entry by email until 5 PM on November 22 (Early Decision and Early Action) or January 15 (Regular Decision). Admitted students should plan to enroll in the school they applied to.',
        source: 'instructions',
    },

    deadlines: {
        entering: 'Fall 2027',
        headline: { label: 'Regular Decision', value: 'January 5' },
        rows: [
            { plan: 'Early Decision', deadline: 'November 1', decision: 'By December 15', binding: true },
            { plan: 'Early Action', deadline: 'November 1', decision: 'By February 15', binding: false },
            { plan: 'Regular Decision', deadline: 'January 5', decision: 'By April 1', binding: false },
        ],
        note: 'Teacher and counselor materials are due November 8 for both early plans and January 10 for Regular Decision. To change an Early Decision or Early Action plan choice, email UVA by 5 PM on November 6. Early Action is non-binding and unrestrictive, and Early Action and Regular Decision admits have until May 1 to reserve a place. UVA says there is no advantage to choosing one plan over another.',
        source: 'instructions',
        binding: 'Early Decision is binding: students admitted must cancel their applications elsewhere and enroll at UVA, and are expected to submit deposits by January 15.',
        bindingSource: 'instructions',
    },

    testing: {
        entering: 'Fall 2027',
        policy: 'Test-optional',
        blind: false,
        line: 'If you are applying for first-year admission for fall 2027, you choose whether to share SAT or ACT scores, and UVA says you will not be disadvantaged by either choice. If you send scores, UVA considers the best combination of them. You can change your test optional decision by email until 5 PM on November 22 (early plans) or January 15 (Regular Decision).',
        source: 'instructions',
        cdsLine: 'The Common Data Set also marks the SAT or ACT as considered if submitted, not required, for fall 2027 applicants, with scores due by January 15.',
        cdsWhere: 'section C8',
    },

    // The next cycle (2027-28, fall 2028 entry): only what the school has
    // announced, read on the date below; nothing announced says so (#2651).
    nextCycle: {
        entering: 'Fall 2028',
        checked: '2026-10-01',
        changes: [],
        source: 'instructions',
    },

    cost: {
        year: '2026-2027',
        columns: ['In-state', 'Out-of-state'],
        rows: [
            { item: 'Tuition', amounts: [16842, 57432] },
            { item: 'Fees', amounts: [3940, 4622] },
            { item: 'Housing', amounts: [8730, 8730] },
            { item: 'Food', amounts: [7340, 7340] },
            { item: 'Books, course materials, supplies and equipment', amounts: [1540, 1540] },
            { item: 'Personal expenses', amounts: [3000, 3000] },
            { item: 'Direct loan fees', amounts: [72, 72] },
            { item: 'Travel', amounts: [550, 550] },
        ],
        totals: [42014, 83286],
        note: 'For a first-year student in the College of Arts and Sciences, fall and spring semesters. Travel for students from outside Virginia runs $550 to $2,240 by region, so the out-of-state total runs $83,286 to $84,976. Tuition differs by school: a first-year Engineering student pays $27,906 in-state and $68,948 out-of-state. Students whose own health insurance does not qualify are enrolled in the UVA plan, $4,220 for the year.',
        source: 'coa',
        netPrice: {
            average: 18056,
            byIncome: [
                { income: '$0 to $30,000', amount: 9479 },
                { income: '$30,001 to $48,000', amount: 8203 },
                { income: '$48,001 to $75,000', amount: 13391 },
                { income: '$75,001 to $110,000', amount: 17879 },
                { income: 'Over $110,000', amount: 33994 },
            ],
            note: 'What first-year, full-time, in-state students who received federal aid paid after grants and scholarships, by family income, in 2024-25, the latest year the Department of Education publishes. Your own price comes from UVA\'s aid offer.',
            tileDetail: 'Per year, after grants, in-state federal aid recipients',
            source: 'scorecard',
        },
    },
};

export default college;
